import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Componente de Calibração do Sonômetro
 * Permite calibrar o dispositivo usando um calibrador acústico padrão (geralmente 94 dB)
 */
const Calibracao = ({ onCalibrationComplete, currentOffset }) => {
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationDb, setCalibrationDb] = useState(94); // Valor padrão do calibrador
  const [measuredValues, setMeasuredValues] = useState([]);
  const [averageDb, setAverageDb] = useState(null);
  const [calculatedOffset, setCalculatedOffset] = useState(currentOffset || 0);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  const valuesRef = useRef([]);

  const CALIBRATION_DURATION = 10; // 10 segundos de calibração

  /**
   * Calcula dB a partir dos dados brutos (sem offset)
   */
  const calculateRawDb = useCallback((dataArray) => {
    let sumSquares = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalized = (dataArray[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    const rms = Math.sqrt(sumSquares / dataArray.length);
    return rms > 0 ? 20 * Math.log10(rms) + 90 : 0;
  }, []);

  /**
   * Loop de captura durante calibração
   */
  const captureAudio = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    const rawDb = calculateRawDb(dataArray);
    valuesRef.current.push(rawDb);
    setMeasuredValues([...valuesRef.current]);

    animationRef.current = requestAnimationFrame(captureAudio);
  }, [calculateRawDb]);

  /**
   * Inicia processo de calibração
   */
  const startCalibration = useCallback(async () => {
    try {
      setError(null);
      setIsCalibrating(true);
      valuesRef.current = [];
      setMeasuredValues([]);
      setAverageDb(null);
      setCountdown(CALIBRATION_DURATION);

      // Solicita acesso ao microfone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      // Cria contexto de áudio
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();

      // Cria analisador
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      analyserRef.current.smoothingTimeConstant = 0.3;

      // Conecta
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Inicia captura
      captureAudio();

      // Timer de countdown
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            finishCalibration();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (err) {
      console.error('Erro na calibração:', err);
      setError(err.message || 'Erro ao acessar microfone');
      setIsCalibrating(false);
    }
  }, [captureAudio]);

  /**
   * Finaliza calibração e calcula offset
   */
  const finishCalibration = useCallback(() => {
    // Para a captura
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    // Calcula média dos valores capturados
    const values = valuesRef.current;
    if (values.length > 0) {
      // Remove outliers (10% inferior e superior)
      const sorted = [...values].sort((a, b) => a - b);
      const trimStart = Math.floor(sorted.length * 0.1);
      const trimEnd = Math.floor(sorted.length * 0.9);
      const trimmed = sorted.slice(trimStart, trimEnd);

      const avg = trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
      setAverageDb(Math.round(avg * 10) / 10);

      // Calcula offset: diferença entre valor esperado e medido
      const offset = calibrationDb - avg;
      setCalculatedOffset(Math.round(offset * 10) / 10);
    }

    setIsCalibrating(false);
  }, [calibrationDb]);

  /**
   * Aplica calibração
   */
  const applyCalibration = useCallback(() => {
    if (onCalibrationComplete) {
      onCalibrationComplete(calculatedOffset);
    }
  }, [onCalibrationComplete, calculatedOffset]);

  /**
   * Reseta calibração
   */
  const resetCalibration = useCallback(() => {
    setCalculatedOffset(0);
    if (onCalibrationComplete) {
      onCalibrationComplete(0);
    }
  }, [onCalibrationComplete]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, []);

  // Valor atual em tempo real durante calibração
  const currentMeasured = measuredValues.length > 0 
    ? Math.round(measuredValues[measuredValues.length - 1] * 10) / 10 
    : null;

  return (
    <div className="calibracao-container">
      <div className="calibracao-header">
        <h3>⚙️ Calibração do Dispositivo</h3>
        <p className="calibracao-info">
          Posicione o microfone do dispositivo junto ao calibrador acústico e inicie a calibração.
          O processo levará {CALIBRATION_DURATION} segundos.
        </p>
      </div>

      <div className="calibracao-config">
        <label htmlFor="calibrationDb">
          Valor do Calibrador (dB):
          <input
            id="calibrationDb"
            type="number"
            value={calibrationDb}
            onChange={(e) => setCalibrationDb(parseFloat(e.target.value) || 94)}
            min="80"
            max="120"
            step="0.1"
            disabled={isCalibrating}
          />
        </label>
        <small>Valor padrão: 94 dB (verifique a especificação do seu calibrador)</small>
      </div>

      {error && (
        <div className="calibracao-error">
          ❌ {error}
        </div>
      )}

      {isCalibrating && (
        <div className="calibracao-progress">
          <div className="countdown-circle">
            <span className="countdown-number">{countdown}</span>
            <span className="countdown-label">segundos</span>
          </div>
          {currentMeasured !== null && (
            <div className="current-measurement">
              <span className="measurement-label">Medido:</span>
              <span className="measurement-value">{currentMeasured} dB</span>
            </div>
          )}
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((CALIBRATION_DURATION - countdown) / CALIBRATION_DURATION) * 100}%` }}
            />
          </div>
        </div>
      )}

      {!isCalibrating && averageDb !== null && (
        <div className="calibracao-result">
          <h4>Resultado da Calibração</h4>
          <div className="result-grid">
            <div className="result-item">
              <span className="result-label">Valor Esperado:</span>
              <span className="result-value">{calibrationDb} dB</span>
            </div>
            <div className="result-item">
              <span className="result-label">Valor Medido (média):</span>
              <span className="result-value">{averageDb} dB</span>
            </div>
            <div className="result-item highlight">
              <span className="result-label">Offset Calculado:</span>
              <span className="result-value">{calculatedOffset > 0 ? '+' : ''}{calculatedOffset} dB</span>
            </div>
          </div>
          <div className="result-samples">
            Amostras coletadas: {measuredValues.length}
          </div>
        </div>
      )}

      <div className="calibracao-status">
        <div className="status-item">
          <span className="status-label">Offset Atual:</span>
          <span className={`status-value ${currentOffset !== 0 ? 'calibrated' : ''}`}>
            {currentOffset > 0 ? '+' : ''}{currentOffset || 0} dB
          </span>
        </div>
      </div>

      <div className="calibracao-actions">
        {!isCalibrating ? (
          <>
            <button 
              className="btn btn-primary"
              onClick={startCalibration}
            >
              🎤 Iniciar Calibração
            </button>
            
            {averageDb !== null && (
              <button 
                className="btn btn-success"
                onClick={applyCalibration}
              >
                ✓ Aplicar Calibração
              </button>
            )}
            
            <button 
              className="btn btn-secondary"
              onClick={resetCalibration}
              disabled={currentOffset === 0}
            >
              ↺ Resetar
            </button>
          </>
        ) : (
          <button 
            className="btn btn-danger"
            onClick={finishCalibration}
          >
            ⏹ Parar Calibração
          </button>
        )}
      </div>

      <div className="calibracao-tips">
        <h5>💡 Dicas para Calibração</h5>
        <ul>
          <li>Use um ambiente silencioso</li>
          <li>Mantenha o calibrador estável durante todo o processo</li>
          <li>Certifique-se de que a cavidade do calibrador está bem acoplada ao microfone</li>
          <li>Verifique se o calibrador está com bateria suficiente</li>
          <li>A frequência padrão de calibração é 1 kHz</li>
        </ul>
      </div>
    </div>
  );
};

export default Calibracao;
